<template>
  <div
    ref="rootRef"
    class="ui-select ui-multi-select"
    :class="attrs.class"
    :style="attrs.style as StyleValue"
  >
    <button
      v-bind="buttonAttrs"
      :id="triggerId"
      ref="triggerRef"
      type="button"
      class="ui-select__trigger"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-activedescendant="activeDescendantId"
      :disabled="disabled"
      @click="onTriggerClick"
      @keydown="onKeydown"
    >
      <span
        ref="valueRef"
        class="ui-select__value"
        :class="{ 'is-placeholder': selectedOptions.length === 0 }"
      >
        {{ summary }}
      </span>

      <ChevronDown class="ui-select__chevron" :size="18" :stroke-width="2.4" aria-hidden="true" />
    </button>

    <Teleport to="body">
      <Transition name="ui-pop">
        <div
          v-if="isOpen"
          ref="panelRef"
          class="ui-select__list ui-select__list--multi"
          :style="panelStyle"
          @mousedown="onPanelMouseDown"
        >
          <div v-if="isSearchEnabled" class="ui-select__search">
            <Search :size="16" :stroke-width="2.2" aria-hidden="true" />

            <input
              ref="searchRef"
              v-model="query"
              type="text"
              class="ui-select__search-input"
              :placeholder="$t('Search')"
              :aria-label="$t('Search')"
              @keydown="onKeydown"
            />
          </div>

          <template v-if="isSelectAllShown">
            <div
              :id="allRowId"
              class="ui-select__option ui-select__all"
              :class="{ 'is-active': isAllRowActive }"
              role="checkbox"
              :aria-checked="allCheckedState"
              @click="toggleAll"
              @mousemove="isAllRowActive = true"
            >
              <UiSelectCheck
                :checked="areAllVisibleSelected"
                :indeterminate="selectedOptions.length > 0"
              />

              <span class="ui-select__text">{{ $t("All") }}</span>
            </div>

            <div class="ui-select__separator" role="separator" />
          </template>

          <ul
            :id="listboxId"
            class="ui-select__options"
            role="listbox"
            aria-multiselectable="true"
            :aria-labelledby="triggerId"
          >
            <template v-for="(option, index) in visibleOptions" :key="String(option.value)">
              <li v-if="option.isGroupHeader" class="ui-select__group" role="presentation">
                {{ option.text }}
              </li>

              <li
                v-else
                :id="optionId(index)"
                class="ui-select__option"
                :class="{
                  'is-selected': isSelected(option.value),
                  'is-active': !isAllRowActive && index === activeIndex,
                  'is-disabled': option.disabled,
                }"
                role="option"
                :aria-selected="isSelected(option.value)"
                :aria-disabled="option.disabled || undefined"
                @click="toggle(option)"
                @mousemove="onOptionHover(option, index)"
              >
                <UiSelectCheck :checked="isSelected(option.value)" />

                <span class="ui-select__text" :title="option.text">{{ option.text }}</span>
              </li>
            </template>

            <li v-if="visibleOptions.length === 0" class="ui-select__empty" role="presentation">
              {{ $t("No options found") }}
            </li>
          </ul>

          <template v-if="isClearShown">
            <div class="ui-select__separator" role="separator" />

            <button
              ref="clearRef"
              type="button"
              class="ui-select__clear"
              :disabled="selectedOptions.length === 0"
              @click="clearAll"
              @keydown="onClearKeydown"
            >
              {{ $t("Clear") }}
            </button>
          </template>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, useId, watch } from "vue";
import type { StyleValue } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronDown, Search } from "lucide-vue-next";
import UiSelectCheck from "@/components/newUi/forms/UiSelectCheck.vue";
import { useAnchoredPopover } from "@/components/newUi/core/useAnchoredPopover";
import {
  isSelectableOption,
  useSelectList,
  type SelectListOption,
} from "@/components/newUi/forms/useSelectList";

type OptionValue = string | number | null;

const MAX_NAMES = 3;

const SELECT_ALL_FROM_OPTIONS = 3;

const PANEL_MAX_HEIGHT = 440;

const PANEL_MIN_WIDTH = 200;

const toPixels = (value: string): number => Number.parseFloat(value) || 0;

// The options list shrinks to the room left on screen, so the height the panel wants is measured from its parts.
const naturalPanelHeight = (panel: HTMLElement): number => {
  const panelStyle = getComputedStyle(panel);
  let height =
    toPixels(panelStyle.paddingTop) +
    toPixels(panelStyle.paddingBottom) +
    toPixels(panelStyle.borderTopWidth) +
    toPixels(panelStyle.borderBottomWidth);

  for (const child of Array.from(panel.children) as HTMLElement[]) {
    const childStyle = getComputedStyle(child);
    const maxHeight = Number.parseFloat(childStyle.maxHeight);
    const isOptionsList = child.classList.contains("ui-select__options");
    const content =
      isOptionsList && Number.isFinite(maxHeight)
        ? Math.min(child.scrollHeight, maxHeight)
        : child.offsetHeight;

    height += content + toPixels(childStyle.marginTop) + toPixels(childStyle.marginBottom);
  }

  return height;
};

const props = withDefaults(
  defineProps<{
    modelValue?: OptionValue[];
    options: SelectListOption[];
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    showSelectAll?: boolean;
  }>(),
  {
    modelValue: () => [],
    placeholder: "",
    disabled: false,
    searchable: false,
    showSelectAll: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: OptionValue[]];
}>();

defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const attrs = useAttrs();
const baseId = useId();
const listboxId = `ui-multi-select-list-${baseId}`;
const allRowId = `${listboxId}-all`;
const triggerId = computed(() => (attrs.id as string | undefined) ?? `ui-multi-select-${baseId}`);
const buttonAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== "class" && key !== "style")),
);
const {
  rootRef,
  triggerRef,
  panelRef,
  isOpen,
  panelStyle,
  open: openPopover,
  close,
} = useAnchoredPopover({
  matchWidth: true,
  maxHeight: PANEL_MAX_HEIGHT,
  minWidth: PANEL_MIN_WIDTH,
  measureContent: (panel) => naturalPanelHeight(panel),
});
const searchRef = ref<HTMLInputElement | null>(null);
const clearRef = ref<HTMLButtonElement | null>(null);
const valueRef = ref<HTMLElement | null>(null);
const valueWidth = ref(0);
const activeIndex = ref(0);
const isAllRowActive = ref(false);

const { query, isSearchEnabled, visibleOptions, firstSelectableIndex, stepSelectableIndex } =
  useSelectList(
    () => props.options,
    () => props.searchable,
  );

const selectableOptions = computed(() => props.options.filter(isSelectableOption));

const selectableVisible = computed(() => visibleOptions.value.filter(isSelectableOption));

const isSelectAllShown = computed(
  () =>
    props.showSelectAll &&
    selectableOptions.value.length >= SELECT_ALL_FROM_OPTIONS &&
    selectableVisible.value.length > 0,
);

const isClearShown = computed(
  () => props.showSelectAll && selectableOptions.value.length >= SELECT_ALL_FROM_OPTIONS,
);

const selectedOptions = computed(() =>
  props.options.filter(
    (option) => !option.isGroupHeader && props.modelValue.includes(option.value),
  ),
);

const areAllVisibleSelected = computed(
  () =>
    selectableVisible.value.length > 0 &&
    selectableVisible.value.every((option) => props.modelValue.includes(option.value)),
);

const allCheckedState = computed<"true" | "false" | "mixed">(() => {
  if (areAllVisibleSelected.value) {
    return "true";
  }

  return selectedOptions.value.length > 0 ? "mixed" : "false";
});

const namesSummary = (shownCount: number): string => {
  const names = selectedOptions.value.slice(0, shownCount).map((option) => option.text);
  const hiddenCount = selectedOptions.value.length - shownCount;

  return hiddenCount > 0 ? `${names.join(", ")} +${hiddenCount}` : names.join(", ");
};

const measureText = (text: string): number | null => {
  if (!valueRef.value) {
    return null;
  }

  const context = document.createElement("canvas").getContext?.("2d");

  if (!context) {
    return null;
  }

  context.font = getComputedStyle(valueRef.value).font;

  return context.measureText(text).width;
};

const fittingNamesSummary = (): string => {
  const availableWidth = valueWidth.value;

  for (let shownCount = selectedOptions.value.length; shownCount > 1; shownCount--) {
    const candidate = namesSummary(shownCount);
    const width = measureText(candidate);

    if (width === null || availableWidth === 0 || width <= availableWidth) {
      return candidate;
    }
  }

  return namesSummary(1);
};

const summary = computed(() => {
  if (selectedOptions.value.length === 0) {
    return props.placeholder;
  }

  if (
    props.showSelectAll &&
    selectableOptions.value.length >= SELECT_ALL_FROM_OPTIONS &&
    selectableOptions.value.every((option) => props.modelValue.includes(option.value))
  ) {
    return t("All");
  }

  if (selectedOptions.value.length <= MAX_NAMES) {
    return fittingNamesSummary();
  }

  return t("Selected: {count}", { count: selectedOptions.value.length });
});

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (!valueRef.value || typeof ResizeObserver === "undefined") {
    return;
  }

  resizeObserver = new ResizeObserver(([entry]) => {
    valueWidth.value = entry.contentRect.width;
  });

  resizeObserver.observe(valueRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

const optionId = (index: number): string => `${listboxId}-option-${index}`;

const activeDescendantId = computed(() => {
  if (!isOpen.value) {
    return undefined;
  }

  if (isAllRowActive.value) {
    return allRowId;
  }

  return activeIndex.value >= 0 ? optionId(activeIndex.value) : undefined;
});

const isSelected = (value: OptionValue): boolean => props.modelValue.includes(value);

const scrollActiveIntoView = (): void => {
  panelRef.value?.querySelector(".is-active")?.scrollIntoView?.({ block: "nearest" });
};

const open = async (): Promise<void> => {
  if (props.disabled || isOpen.value) {
    return;
  }

  query.value = "";
  activeIndex.value = firstSelectableIndex();
  isAllRowActive.value = false;

  openPopover();

  await nextTick();

  searchRef.value?.focus();
};

const toggle = (option: SelectListOption): void => {
  if (!isSelectableOption(option)) {
    return;
  }

  emit(
    "update:modelValue",
    isSelected(option.value)
      ? props.modelValue.filter((value) => value !== option.value)
      : [...props.modelValue, option.value],
  );
};

const clearAll = (): void => {
  emit("update:modelValue", []);

  if (document.activeElement === clearRef.value) {
    triggerRef.value?.focus();
  }
};

const toggleAll = (): void => {
  const visibleValues = selectableVisible.value.map((option) => option.value);

  if (areAllVisibleSelected.value) {
    emit(
      "update:modelValue",
      props.modelValue.filter((value) => !visibleValues.includes(value)),
    );

    return;
  }

  emit("update:modelValue", [
    ...props.modelValue,
    ...visibleValues.filter((value) => !props.modelValue.includes(value)),
  ]);
};

const onTriggerClick = (): void => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const onPanelMouseDown = (event: MouseEvent): void => {
  if (!(event.target as HTMLElement).closest(".ui-select__search-input")) {
    event.preventDefault();
  }
};

const onOptionHover = (option: SelectListOption, index: number): void => {
  if (isSelectableOption(option)) {
    isAllRowActive.value = false;
    activeIndex.value = index;
  }
};

const move = (delta: number): void => {
  if (isAllRowActive.value) {
    if (delta > 0) {
      isAllRowActive.value = false;
      activeIndex.value = firstSelectableIndex();
    }
  } else {
    const nextIndex = stepSelectableIndex(activeIndex.value, delta);

    if (delta < 0 && nextIndex === activeIndex.value && isSelectAllShown.value) {
      isAllRowActive.value = true;
    } else {
      activeIndex.value = nextIndex;
    }
  }

  nextTick(scrollActiveIntoView);
};

const onKeydown = (event: KeyboardEvent): void => {
  const isSearchInput = (event.target as HTMLElement).classList.contains("ui-select__search-input");

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();

      if (isOpen.value) {
        move(1);
      } else {
        open();
      }
      break;
    case "ArrowUp":
      event.preventDefault();

      if (isOpen.value) {
        move(-1);
      } else {
        open();
      }
      break;
    case "Enter":
    case " ":
      if (event.key === " " && isSearchInput) {
        break;
      }

      event.preventDefault();

      if (isOpen.value) {
        if (isAllRowActive.value) {
          toggleAll();
        } else {
          const option = visibleOptions.value[activeIndex.value];

          if (option) {
            toggle(option);
          }
        }
      } else {
        open();
      }
      break;
    case "Escape":
      if (isOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        close();
        triggerRef.value?.focus();
      }
      break;
    case "Tab":
      if (isOpen.value && !event.shiftKey && clearRef.value && !clearRef.value.disabled) {
        event.preventDefault();
        clearRef.value.focus();
      } else {
        close();

        if (event.target !== triggerRef.value) {
          triggerRef.value?.focus();
        }
      }
      break;
  }
};

const onClearKeydown = (event: KeyboardEvent): void => {
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    close();
    triggerRef.value?.focus();

    return;
  }

  if (event.key !== "Tab") {
    return;
  }

  event.preventDefault();

  if (event.shiftKey) {
    (searchRef.value ?? triggerRef.value)?.focus();

    return;
  }

  close();
  triggerRef.value?.focus();
};

watch(query, () => {
  activeIndex.value = firstSelectableIndex();
  isAllRowActive.value = false;
});

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      close();
    }
  },
);
</script>
